import { useRef, useState } from "react";
import { Loader2, MessageSquare, Radio, Send } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { casesApi, type EncounterRead } from "@/features/cases/cases.api";
import { ssePost } from "@/lib/sse";

interface ChatPanelProps {
  encounterId: string;
  disabled: boolean;
  onUpdate: (e: EncounterRead) => void;
}

const STAGE_LABEL: Record<string, string> = {
  scrubbing: "Limpiando PII del mensaje...",
  thinking: "El paciente está pensando su respuesta...",
  generating: "Construyendo respuesta...",
  done: "Listo",
};

export function ChatPanel({ encounterId, disabled, onUpdate }: ChatPanelProps) {
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [stage, setStage] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [costInfo, setCostInfo] = useState<{
    cost?: number;
    latency?: number;
    provider?: string;
  } | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  async function onSend() {
    if (!message.trim()) return;
    setSending(true);
    setStage(null);
    setPreview(null);
    setCostInfo(null);
    const sent = message;
    setMessage("");

    const ac = new AbortController();
    abortRef.current = ac;

    let gotResult = false;

    try {
      await ssePost(`/cases/encounters/${encounterId}/message/stream`, {
        body: { message: sent },
        signal: ac.signal,
        onEvent: ({ event, data }) => {
          try {
            const parsed = JSON.parse(data);
            if (event === "stage") {
              setStage(parsed.stage);
            } else if (event === "token") {
              setPreview(parsed.delta);
              setCostInfo({
                cost: parsed.cost_usd,
                latency: parsed.latency_ms,
                provider: parsed.provider,
              });
            } else if (event === "result") {
              gotResult = true;
              if (parsed.encounter) onUpdate(parsed.encounter);
            }
          } catch {
            /* evento malformado — ignorar */
          }
        },
        onError: (err) => {
          toast.error(`SSE falló: ${err.message}. Probando modo sync...`);
        },
      });

      // Fallback: si el SSE no devolvió el result completo, refresca
      if (!gotResult) {
        const fresh = await casesApi.getEncounter(encounterId);
        onUpdate(fresh);
      }
    } catch (err) {
      // Fallback completo al endpoint sync
      try {
        const sync = await casesApi.message(encounterId, sent);
        onUpdate(sync);
        toast.info("Respuesta vía endpoint síncrono");
      } catch (e) {
        toast.error(
          e instanceof Error ? e.message : "Error al enviar mensaje",
        );
      }
    } finally {
      setSending(false);
      setStage(null);
      setPreview(null);
      abortRef.current = null;
    }
  }

  function onCancel() {
    abortRef.current?.abort();
    abortRef.current = null;
    setSending(false);
    setStage(null);
    setPreview(null);
  }

  return (
    <div className="space-y-3">
      <header className="space-y-1">
        <div className="flex items-center gap-2 font-[var(--font-display)] text-sm font-bold">
          <MessageSquare size={14} className="text-[var(--color-blue-soft)]" />
          Hablar con el paciente
        </div>
        <p className="text-[11.5px] text-[var(--color-text-muted)]">
          El paciente responde en el registro lingüístico de su receta. Usa streaming SSE
          con fallback síncrono automático.
        </p>
      </header>

      <Textarea
        rows={4}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        disabled={disabled || sending}
        placeholder="Ej: ¿Cuándo empezó el dolor? ¿Dónde se irradia?"
      />

      {stage && (
        <div className="flex items-center gap-2 rounded-[var(--radius-md)] border border-[var(--color-blue)]/25 bg-[var(--color-blue)]/5 px-3 py-2">
          <Radio size={12} className="ping-dot text-[var(--color-blue-soft)]" />
          <span className="text-[12px] text-[var(--color-text-primary)]">
            {STAGE_LABEL[stage] ?? stage}
          </span>
        </div>
      )}

      {preview && (
        <div className="rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface-2)]/60 p-3">
          <div className="mb-1 flex items-center gap-1.5 font-[var(--font-mono)] text-[9px] uppercase tracking-widest text-[var(--color-text-muted)]">
            <span>Paciente</span>
            {costInfo?.provider && (
              <Badge variant="outline" size="sm">
                {costInfo.provider}
              </Badge>
            )}
            {costInfo?.latency && (
              <span>{costInfo.latency}ms</span>
            )}
            {typeof costInfo?.cost === "number" && costInfo.cost > 0 && (
              <span>${costInfo.cost.toFixed(5)}</span>
            )}
          </div>
          <div className="whitespace-pre-wrap text-[13px] text-[var(--color-text-primary)]">
            {preview}
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <Button
          onClick={onSend}
          disabled={disabled || sending || message.trim().length === 0}
          className="flex-1"
        >
          {sending ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
          {sending ? "Procesando..." : "Enviar"}
        </Button>
        {sending && (
          <Button variant="outline" onClick={onCancel}>
            Cancelar
          </Button>
        )}
      </div>
    </div>
  );
}
